/**
 * §6.3 asset upload UI: presign → PUT → finalize with alt text and a rights confirmation (§17.1 "signed direct
 * upload, type and content validation"). `SwatchBulkUpload` uploads several images for one colour at a time
 * and reports the resulting `asset:` refs so the builder can assign them to every SKU of that colour.
 */
import type { AssetDto, AssetPurpose } from '@windrop/contracts';
import { ASSET_RULES } from '@windrop/contracts';
import { Button, Checkbox, Field, Input, Upload, type UploadState } from '@windrop/ui';
import { useState } from 'react';
import { isApiError } from '../../lib/api/client';
import { uploadAsset } from './api';

export interface AssetUploaderProps {
  purpose: AssetPurpose;
  label: string;
  /** Alt text default (e.g. "<product> in <colour>"); the operator can edit it before upload. */
  altSuggestion?: string;
  onUploaded: (asset: AssetDto) => void;
  multiple?: boolean;
  /** Test seam. */
  upload?: typeof uploadAsset;
}

export function AssetUploader({
  purpose,
  label,
  altSuggestion = '',
  onUploaded,
  multiple = false,
  upload = uploadAsset,
}: AssetUploaderProps) {
  const rule = ASSET_RULES[purpose];
  const [alt, setAlt] = useState(altSuggestion);
  const [rights, setRights] = useState(false);
  const [queue, setQueue] = useState<File[]>([]);
  const [state, setState] = useState<UploadState>('idle');
  const [reason, setReason] = useState<string | undefined>();
  const [done, setDone] = useState<AssetDto[]>([]);

  const ready = alt.trim().length > 0 && rights && queue.length > 0 && state !== 'uploading';

  const run = async () => {
    setState('uploading');
    setReason(undefined);
    const results: AssetDto[] = [];
    try {
      for (const file of queue) {
        const asset = await upload(file, purpose, alt.trim());
        results.push(asset);
        onUploaded(asset);
      }
      setDone((d) => [...d, ...results]);
      setQueue([]);
      setState('clean');
    } catch (e) {
      setState('rejected');
      setReason(isApiError(e) ? e.message : 'The file could not be checked. Try another file.');
    }
  };

  return (
    <div className="wd-stack" data-testid={`asset-uploader-${purpose}`}>
      <Upload
        label={label}
        accept={[...rule.allowedTypes]}
        maxBytes={rule.maxBytes}
        state={state}
        reason={reason}
        fileName={queue.map((f) => f.name).join(', ') || undefined}
        hint={
          rule.minWidth
            ? `At least ${rule.minWidth}×${rule.minHeight} px. ${multiple ? 'Add one file at a time; all queued files share the alt text.' : ''}`
            : undefined
        }
        onFile={(file) => {
          setState('idle');
          setQueue((q) => (multiple ? [...q, file] : [file]));
        }}
        onRemove={() => {
          setQueue([]);
          setState('idle');
        }}
      />
      <Field
        label="Alt text"
        hint="Describes the image for screen readers and when it fails to load (§13.5)."
        required
      >
        <Input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={240} data-testid="asset-alt" />
      </Field>
      <Checkbox
        label="I confirm we hold the rights to publish this image"
        description="Sponsor-supplied imagery needs the sponsor's written permission on file (§6.1)."
        checked={rights}
        onCheckedChange={setRights}
        data-testid="asset-rights"
      />
      <div>
        <Button
          variant="secondary"
          onClick={run}
          disabled={!ready}
          loading={state === 'uploading'}
          data-testid="asset-upload-run"
        >
          Upload {queue.length > 1 ? `${queue.length} files` : 'and validate'}
        </Button>
      </div>
      {done.length ? (
        <ul className="wd-muted" data-testid="asset-uploaded">
          {done.map((a) => (
            <li key={a.assetId}>
              {a.ref} · {a.width}×{a.height} {a.format}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export interface SwatchBulkUploadProps {
  productName: string;
  /** Colour axis values (valueId + label + swatch). */
  colours: { valueId: string; label: string; swatch?: string }[];
  onAssigned: (colourValueId: string, refs: string[]) => void;
  upload?: typeof uploadAsset;
}

/** Per-colour bulk image upload: pick a swatch, drop its images, every SKU in that colour gets the refs. */
export function SwatchBulkUpload({ productName, colours, onAssigned, upload }: SwatchBulkUploadProps) {
  const [colour, setColour] = useState(colours[0]?.valueId ?? '');
  const current = colours.find((c) => c.valueId === colour);
  if (!colours.length) return <p className="wd-muted">Add a colour axis to upload images per colour.</p>;
  return (
    <div className="wd-stack" data-testid="swatch-bulk-upload">
      <div role="radiogroup" aria-label="Colour" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {colours.map((c) => (
          <label
            key={c.valueId}
            className="wd-chip"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <input
              type="radio"
              name="swatch"
              value={c.valueId}
              checked={colour === c.valueId}
              onChange={() => setColour(c.valueId)}
            />
            <span
              aria-hidden
              style={{
                width: 16,
                height: 16,
                borderRadius: 999,
                background: c.swatch ?? 'var(--wd-border)',
                display: 'inline-block',
              }}
            />
            {c.label}
          </label>
        ))}
      </div>
      {current ? (
        <AssetUploader
          key={current.valueId}
          purpose="PRIZE_IMAGE"
          label={`Images for ${current.label}`}
          altSuggestion={`${productName} in ${current.label}`}
          multiple
          upload={upload}
          onUploaded={(asset) => onAssigned(current.valueId, [asset.ref])}
        />
      ) : null}
    </div>
  );
}
